import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Nav,
  NavItem,
  NavLink,
  Row,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";

import AppModal from "../../../../Components/Common/AppModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import NoDataContent from "../../../../Components/Common/NoDataContent";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";
import { SettingsSubTabContext } from "../../settingsTabContext";
import RenewalWhen from "../../../../Components/Common/RenewalWhen";
import AppSelect from "../../../../Components/Common/AppSelect";

// Super Admin > Subscription Licences (CRMit Task 24878).
//
// Cross company maintenance of SubscriptionLicence in ZCRM_Master. The
// /subscriptionlicence/* endpoints are scoped to the caller's own company, so
// this screen talks to /subscriptionlicenceadmin/* instead.
//
// There is NO delete. A licence is retired by setting an End Date; the row is
// history and has to survive. That is why the Expired tab exists.
//
// The Overdue tab should normally be EMPTY. Anything sitting in it has a
// renewal date in the past, which means the nightly cron
// (ZCronController::CrmitCollectSubscriptionCardPayments) will bill its arrears
// on the next run: one invoice line per outstanding period, charged in one go.

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const TABS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "overdue", label: "Overdue" },
  { key: "due7", label: "Due in 7 days" },
  // Change #100463 - billing paused: not Overdue / Due, never charged.
  { key: "paused", label: "Paused" },
  { key: "expired", label: "Expired" },
];

const dash = <span className="text-muted">&mdash;</span>;

/** 2026-09-22 01:03:38 -> 22/09/2026 01:03 */
const fmtDateTime = (v) => {
  if (!v) return dash;
  const d = new Date(String(v).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(v);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(
    d.getHours()
  )}:${p(d.getMinutes())}`;
};

const fmtMoney = (v) =>
  v === null || v === undefined || v === ""
    ? dash
    : `£${Number(v).toFixed(2)}`;

/** 'Y-m-d H:i:s' -> value for <input type="datetime-local"> */
const toInput = (v) => {
  if (!v) return "";
  return String(v).replace(" ", "T").slice(0, 16);
};

/** value from <input type="datetime-local"> -> 'Y-m-d H:i:s' */
const fromInput = (v) => {
  if (!v) return "";
  const s = String(v).replace("T", " ");
  return s.length === 16 ? `${s}:00` : s;
};

/**
 * Whole months a renewal date is in arrears.
 *
 * Mirrors the cron's catch-up walk, so this is the number of line items the
 * arrears invoice will carry. Capped, so a wildly wrong date cannot spin.
 */
const monthsBehind = (renewalDateTime) => {
  if (!renewalDateTime) return 0;
  const now = new Date();
  const walk = new Date(String(renewalDateTime).replace(" ", "T"));
  if (Number.isNaN(walk.getTime())) return 0;
  let n = 0;
  while (walk < now && n < 120) {
    walk.setMonth(walk.getMonth() + 1);
    n += 1;
  }
  return n;
};

const nowForInput = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours()
  )}:${p(d.getMinutes())}`;
};

// Change #100463 - fixedCompanyId locks the screen to one company, for the
// Subscriptions tab on Back End > Company Details.
const SubscriptionLicenceAdmin = ({ fixedCompanyId = null, defaultTab = "overdue", initialTab = null } = {}) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  // Change #100463 - column sort, done on the server (the list is paged).
  const [sortBy, setSortBy] = useState("Company");
  const [sortDir, setSortDir] = useState("ASC");
  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"));
    else {
      setSortBy(key);
      setSortDir("ASC");
    }
    setPage(1);
  };
  // Change #100463 - totals across every matching licence (server side).
  const [totals, setTotals] = useState(null);
  // Companies with licences on this tab (Company filter); null = not sent yet.
  const [companyOptions, setCompanyOptions] = useState(null);
  const [tabCounts, setTabCounts] = useState({});
  const [loading, setLoading] = useState(true);

  const subTabCtx = useContext(SettingsSubTabContext);
  const tabKeys = TABS.map((t) => t.key);
  const urlTab = String((fixedCompanyId ? initialTab : subTabCtx?.subTab) || "").toLowerCase();
  const [tab, setTabState] = useState(tabKeys.includes(urlTab) ? urlTab : defaultTab);
  useEffect(() => {
    if (fixedCompanyId) return;
    const t = String(subTabCtx?.subTab || "").toLowerCase();
    if (tabKeys.includes(t) && t !== tab) setTabState(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTabCtx?.subTab]);
  const setTab = (key) => {
    setTabState(key);
    if (fixedCompanyId) {
      window.history.replaceState(null, "", `/company-details/${fixedCompanyId}/Licences/${key}`);
    } else if (subTabCtx?.setSubTab) {
      subTabCtx.setSubTab(key);
    }
  };
  // Back End: Company Details rewrites the URL when it opens the tab (after
  // this effect), so put the sub tab back on the next tick.
  useEffect(() => {
    if (!fixedCompanyId) return undefined;
    const t = setTimeout(
      () => window.history.replaceState(null, "", `/company-details/${fixedCompanyId}/Licences/${tab}`),
      0
    );
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedCompanyId]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [search, setSearch] = useState("");
  const [companyId, setCompanyId] = useState(fixedCompanyId ? String(fixedCompanyId) : "");
  const [productId, setProductId] = useState("");

  const [options, setOptions] = useState({
    company_list: [],
    subscriptionproduct_list: [],
    subscriptionlicencedurationtype_list: [],
  });

  const [editorOpen, setEditorOpen] = useState(false);
  // Change #100463 - Pause / Resume billing on one licence. { row, resume }.
  const [pauseTarget, setPauseTarget] = useState(null);
  const [resumeDate, setResumeDate] = useState("");
  const [pauseSaving, setPauseSaving] = useState(false);
  const stampNow = () => {
    const d = new Date();
    const z = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())} ${z(d.getHours())}:${z(d.getMinutes())}:${z(d.getSeconds())}`;
  };
  // Change #100463 - Cancel one licence: end it on a date (default today).
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelDate, setCancelDate] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const saveCancel = async () => {
    if (!cancelTarget || !cancelDate) {
      toast.error("Pick the end date");
      return;
    }
    setCancelling(true);
    try {
      const res = await FetchAPI("/subscriptionlicenceadmin/update", "PUT", [
        { Id: cancelTarget.Id, EndDateTime: `${cancelDate} 23:59:59`, FlagConfirmBackdatedRenewal: 1 },
      ]);
      if (Number(res?.status) === 200) {
        toast.success(`Licence #${cancelTarget.Id} cancelled from ${cancelDate.split("-").reverse().join("/")}`);
        setCancelTarget(null);
        load();
      } else {
        toast.error(res?.message || "Could not cancel the licence");
      }
    } catch (e) {
      toast.error("Could not cancel the licence");
    } finally {
      setCancelling(false);
    }
  };

  const savePause = async () => {
    if (!pauseTarget) return;
    const row = pauseTarget.row;
    let body;
    if (pauseTarget.resume) {
      if (!resumeDate) {
        toast.error("Pick the next renewal date");
        return;
      }
      const grace = new Date(`${resumeDate}T00:00:00`);
      grace.setDate(grace.getDate() + (Number(row.RenewalGraceDays) || 3));
      const z = (n) => String(n).padStart(2, "0");
      body = {
        Id: row.Id,
        FlagBillingPaused: 0,
        BillingPausedDateTime: "",
        RenewalDateTime: `${resumeDate} 00:00:00`,
        RenewalGraceDateTime: `${grace.getFullYear()}-${z(grace.getMonth() + 1)}-${z(grace.getDate())} 00:00:00`,
        FlagConfirmBackdatedRenewal: 1,
      };
    } else {
      body = { Id: row.Id, FlagBillingPaused: 1, BillingPausedDateTime: stampNow(), FlagConfirmBackdatedRenewal: 1 };
    }
    setPauseSaving(true);
    try {
      const res = await FetchAPI("/subscriptionlicenceadmin/update", "PUT", [body]);
      if (Number(res?.status) === 200) {
        toast.success(pauseTarget.resume ? "Billing resumed" : "Billing paused. The card run will not charge it until resumed.");
        setPauseTarget(null);
        load();
      } else {
        toast.error(res?.message || "Could not update the licence");
      }
    } catch (e) {
      toast.error("Could not update the licence");
    } finally {
      setPauseSaving(false);
    }
  };
  // Change #100463 - the chosen company's users, for the searchable
  // "Assigned to" picker (Paul: no typing a User Id).
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companyUsersLoading, setCompanyUsersLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Set when a save would backdate the renewal on a live licence. Holds the
  // payload so the second, confirmed attempt sends exactly what was reviewed.
  const [confirmBackdate, setConfirmBackdate] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await FetchAPI("/subscriptionlicenceadmin/search", "POST", [
        {
          Tab: tab,
          Search: search.trim(),
          Company_Id: companyId || null,
          SubscriptionProduct_Id: productId || null,
          Page: page,
          SortBy: sortBy,
          SortDir: sortDir,
          PageSize: pageSize,
        },
      ]);

      if (Number(res?.status) === 200) {
        setRows(res?.subscriptionlicence_list || []);
        setSummary(res?.summary || null);
        setTotals(res?.totals || null);
        setCompanyOptions(Array.isArray(res?.company_options) ? res.company_options : null);
        setTabCounts(res?.tab_counts || {});
      } else {
        setRows([]);
        setSummary(null);
        toast.error(res?.message || "Could not load subscription licences");
      }
    } catch (e) {
      setRows([]);
      setSummary(null);
      toast.error("Could not load subscription licences");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page, pageSize, search, companyId, productId, sortBy, sortDir]);

  // Debounced so typing in the search box fires one request, not one per key.
  useEffect(() => {
    const timer = setTimeout(() => load(), 350);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const res = await FetchAPI("/subscriptionlicenceadmin/options", "GET");
        if (Number(res?.status) === 200) {
          setOptions({
            company_list: res?.company_list || [],
            subscriptionproduct_list: res?.subscriptionproduct_list || [],
            subscriptionlicencedurationtype_list:
              res?.subscriptionlicencedurationtype_list || [],
          });
        }
      } catch (e) {
        // Non fatal: the grid still works, only the dropdowns are empty.
      }
    })();
  }, []);

  useEffect(() => {
    if (!editorOpen || !form.Company_Id) {
      setCompanyUsers([]);
      return;
    }
    let alive = true;
    setCompanyUsersLoading(true);
    FetchAPI("/companytouser/list/mapping", "POST", [{ Company_Id: Number(form.Company_Id), Limit: 5000 }])
      .then((res) => {
        if (!alive) return;
        const rows = Number(res?.status) === 200 ? res?.companytouser_list || [] : [];
        setCompanyUsers(
          rows
            .map((r) => ({
              value: String(r.User_Id),
              label: `${r.User_UserFullName || "User #" + r.User_Id}${r.User_Email ? " (" + r.User_Email + ")" : ""}`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      })
      .catch(() => alive && setCompanyUsers([]))
      .finally(() => alive && setCompanyUsersLoading(false));
    return () => {
      alive = false;
    };
  }, [editorOpen, form.Company_Id]);

  const setField = (name, value) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  const openEditor = async (row) => {
    if (!row) {
      setEditing(null);
      setForm({
        Company_Id: companyId || "",
        SubscriptionProduct_Id: "",
        User_Id: "",
        FlagAssigned: "0",
        SubscriptionLicenceDurationType_Id: "1",
        StartDateTime: nowForInput(),
        RenewalDateTime: "",
        RenewalGraceDateTime: "",
        RenewalGraceDays: "3",
        EndDateTime: "",
        AmountLocalSubTotal: "",
        AmountLocalVat: "",
        AmountLocalTotal: "",
        AmountVatPercentage: "20.00",
        PaymentRetryAttemptsMaximum: "3",
        PaymentRetryAttemptsRemaining: "3",
        PaymentRetryAttemptNextDateTime: "",
      });
      setEditorOpen(true);
      return;
    }

    try {
      const res = await FetchAPI(
        `/subscriptionlicenceadmin/data/${row["Id"]}`,
        "GET"
      );
      if (Number(res?.status) !== 200) {
        toast.error(res?.message || "Could not open that licence");
        return;
      }
      const full = res?.subscriptionlicence_data || {};
      setEditing(full);
      setForm({
        Company_Id: full.Company_Id ?? "",
        SubscriptionProduct_Id: full.SubscriptionProduct_Id ?? "",
        User_Id: full.User_Id ?? "",
        FlagAssigned: String(Number(full.FlagAssigned) === 1 ? 1 : 0),
        SubscriptionLicenceDurationType_Id:
          full.SubscriptionLicenceDurationType_Id ?? "",
        StartDateTime: toInput(full.StartDateTime),
        RenewalDateTime: toInput(full.RenewalDateTime),
        RenewalGraceDateTime: toInput(full.RenewalGraceDateTime),
        RenewalGraceDays: full.RenewalGraceDays ?? "",
        EndDateTime: toInput(full.EndDateTime),
        AmountLocalSubTotal: full.AmountLocalSubTotal ?? "",
        AmountLocalVat: full.AmountLocalVat ?? "",
        AmountLocalTotal: full.AmountLocalTotal ?? "",
        AmountVatPercentage: full.AmountVatPercentage ?? "",
        PaymentRetryAttemptsMaximum: full.PaymentRetryAttemptsMaximum ?? "",
        PaymentRetryAttemptsRemaining: full.PaymentRetryAttemptsRemaining ?? "",
        PaymentRetryAttemptNextDateTime: toInput(
          full.PaymentRetryAttemptNextDateTime
        ),
      });
      setEditorOpen(true);
    } catch (e) {
      toast.error("Could not open that licence");
    }
  };

  /** VAT and total from sub total + VAT %, so the three cannot drift apart. */
  const recalc = () => {
    const sub = Number(form.AmountLocalSubTotal || 0);
    const pct = Number(form.AmountVatPercentage || 0);
    const vat = Math.round(sub * (pct / 100) * 100) / 100;
    setForm((prev) => ({
      ...prev,
      AmountLocalVat: vat.toFixed(2),
      AmountLocalTotal: (sub + vat).toFixed(2),
    }));
  };

  const doSave = async (payload) => {
    setSaving(true);
    try {
      const res = editing
        ? await FetchAPI("/subscriptionlicenceadmin/update", "PUT", [payload])
        : await FetchAPI("/subscriptionlicenceadmin/create", "POST", [payload]);

      if (Number(res?.status) === 200) {
        toast.success(editing ? "Saved" : "Licence created");
        setConfirmBackdate(null);
        setEditorOpen(false);
        setEditing(null);
        await load();
      } else if (res?.RequiresConfirmation) {
        // Safety net. The screen normally catches this before calling, but the
        // endpoint enforces the flag too, so honour a server-side prompt.
        setConfirmBackdate({
          payload,
          months: Number(res?.MonthsBehind || 0),
        });
      } else {
        setConfirmBackdate(null);
        toast.error(res?.message || "Could not save that licence");
      }
    } catch (e) {
      setConfirmBackdate(null);
      toast.error("Could not save that licence");
    }
    setSaving(false);
  };

  const save = async () => {
    const payload = {
      ...form,
      // Change #100463 - not a separate choice any more.
      FlagAssigned: form.User_Id ? 1 : 0,
      StartDateTime: fromInput(form.StartDateTime),
      RenewalDateTime: fromInput(form.RenewalDateTime),
      RenewalGraceDateTime: fromInput(form.RenewalGraceDateTime),
      EndDateTime: fromInput(form.EndDateTime),
      PaymentRetryAttemptNextDateTime: fromInput(
        form.PaymentRetryAttemptNextDateTime
      ),
    };
    if (editing) payload["Id"] = editing["Id"];

    // Backdating a renewal on a LIVE licence is allowed, because billing
    // arrears on purpose is a real thing to want. It is not allowed silently:
    // the next run charges the card for every outstanding period in one go, so
    // it gets the same double confirmation as a delete.
    const endsInFuture =
      !payload.EndDateTime ||
      new Date(String(payload.EndDateTime).replace(" ", "T")) > new Date();
    const months = monthsBehind(payload.RenewalDateTime);

    if (months > 0 && endsInFuture) {
      setConfirmBackdate({
        payload: { ...payload, FlagConfirmBackdatedRenewal: 1 },
        months,
      });
      return;
    }

    await doSave(payload);
  };

  const pageQty = summary?.PageQty || 1;

  const companyName = (id) =>
    options.company_list.find((c) => String(c.Id) === String(id))
      ?.BusinessName || `Company #${id}`;

  return (
    <React.Fragment>
      <DeleteModal
        show={!!confirmBackdate}
        loading={false}
        onCloseClick={() => setConfirmBackdate(null)}
        onDeleteClick={() =>
          doSave({
            ...confirmBackdate.payload,
            FlagConfirmBackdatedRenewal: 1,
          })
        }
        customButtonText="Yes, backdate it"
        customText={
          confirmBackdate ? (
            <span>
              This renewal date is in the past, and the licence is still live,
              so the next renewal run will bill the arrears.
              <br />
              <br />
              <strong>
                {confirmBackdate.months} period
                {confirmBackdate.months === 1 ? "" : "s"}
              </strong>{" "}
              will go on <strong>one invoice</strong> as{" "}
              {confirmBackdate.months === 1 ? "a line item" : "separate line items"}
              , and the card will be charged once for{" "}
              <strong>
                £
                {(
                  confirmBackdate.months *
                  Number(confirmBackdate.payload?.AmountLocalTotal || 0)
                ).toFixed(2)}
              </strong>
              .
              <br />
              <br />
              Only go ahead if you mean to bill the arrears.
            </span>
          ) : null
        }
      />

      <Card>
        <CardHeader>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <div>
              <h5 className="card-title mb-1">
                Subscription Licences{" "}
                <span className="text-muted fs-12">(SubscriptionLicence)</span>
              </h5>
              <p className="text-muted mb-0 fs-13">
                Every company&apos;s licences. Set an end date, correct the next
                renewal date or the pricing, or add a licence. Licences are
                never deleted, so ended ones stay on the Expired tab.
              </p>
            </div>
            <Button color="success" onClick={() => openEditor(null)}>
              <i className="ri-add-line align-bottom me-1"></i>
              Add Licence
            </Button>
          </div>

          <Nav tabs className="nav-tabs-custom mt-3">
            {TABS.map((t) => {
              const count = tabCounts?.[t.key];
              const isOverdue = t.key === "overdue";
              return (
                <NavItem key={t.key}>
                  <NavLink
                    href="#"
                    className={tab === t.key ? "active" : ""}
                    onClick={(e) => {
                      e.preventDefault();
                      setTab(t.key);
                      setPage(1);
                    }}
                  >
                    {t.label}{" "}
                    {count !== undefined && (
                      <Badge
                        color={
                          isOverdue && count > 0
                            ? "danger"
                            : tab === t.key
                            ? "primary"
                            : "light"
                        }
                        className={
                          isOverdue && count > 0
                            ? ""
                            : tab === t.key
                            ? ""
                            : "text-muted"
                        }
                      >
                        {count}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
              );
            })}
          </Nav>

          <Row className="mt-3 g-2 align-items-end">
            <Col sm={6} md={4}>
              <label className="form-label mb-1 fs-13">Search</label>
              <Input
                type="text"
                placeholder="Company, product, user or licence id…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </Col>
            <Col sm={6} md={3}>
              <label className="form-label mb-1 fs-13">Company</label>
              <Input
                type="select"
                value={companyId}
                disabled={!!fixedCompanyId}
                onChange={(e) => {
                  setCompanyId(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All companies</option>
                {/* Change #100463 - only companies with licences on this tab
                    (older backends: every company). */}
                {(companyOptions || options.company_list).map((c) => (
                  <option key={c.Id} value={c.Id}>
                    {c.BusinessName}
                    {Number(c.FlagTest) === 1 ? " (test)" : ""}
                    {c.LicenceQty != null ? ` (${c.LicenceQty})` : ""}
                  </option>
                ))}
              </Input>
            </Col>
            <Col sm={6} md={3}>
              <label className="form-label mb-1 fs-13">Product</label>
              <Input
                type="select"
                value={productId}
                onChange={(e) => {
                  setProductId(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All products</option>
                {options.subscriptionproduct_list.map((p) => (
                  <option key={p.Id} value={p.Id}>
                    {p.NamePrinted}
                  </option>
                ))}
              </Input>
            </Col>
            <Col sm={6} md={2}>
              <label className="form-label mb-1 fs-13">Per page</label>
              <Input
                type="select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>

          {summary && !loading && (
            <p className="text-muted mb-0 mt-2 fs-13">
              {Number(summary.RowQty).toLocaleString()} licence
              {summary.RowQty === 1 ? "" : "s"} &mdash; page {summary.Page} of{" "}
              {pageQty} &mdash; as at {fmtDateTime(summary.AsAt)}
            </p>
          )}
        </CardHeader>

        <CardBody className="pt-0">
          {tab === "overdue" && Number(tabCounts?.overdue) > 0 && (
            <Alert color="danger" className="mt-3 mb-0">
              <strong>This tab should be empty.</strong> Every licence below has
              a renewal date in the past, so the next renewal run will bill its
              arrears: one line per outstanding period, charged to the card in
              one go. Set the next renewal date forward, or end the licence, if
              that is not what you want.
            </Alert>
          )}

          {loading ? (
            <LoadingSpinner />
          ) : rows.length === 0 ? (
            <NoDataContent
              titleText={
                tab === "overdue" ? "Nothing overdue" : "Nothing to show"
              }
              paragraphText={
                tab === "overdue"
                  ? "No licence has a renewal date in the past. This is the state you want."
                  : "No licences matched."
              }
              useButton={false}
            />
          ) : (
            <>
              <div className="table-responsive table-card mt-3">
                <Table className="align-middle table-nowrap mb-0">
                  <thead className="text-muted">
                    <tr>
                      {[
                        ["Id", "Id"],
                        ["Company", "Company"],
                        ["Product", "Product"],
                        ["AssignedTo", "Assigned To"],
                        ["StartDateTime", "Start"],
                        ["RenewalDateTime", "Next Renewal"],
                        ["RenewalGraceDateTime", "Grace"],
                        ["EndDateTime", "End"],
                        ["AmountLocalTotal", "Total", "text-end"],
                        ["Duration", "Duration"],
                        ["LastRenewalDateTime", "Last Renewal"],
                      ].map(([key, label, cls]) => (
                        <th
                          key={key}
                          className={`${cls || ""} user-select-none`}
                          style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                          title={`Sort by ${label}`}
                          onClick={() => toggleSort(key)}
                        >
                          {label}
                          <i
                            className={`ms-1 ${
                              sortBy === key
                                ? sortDir === "ASC"
                                  ? "ri-arrow-up-s-fill text-primary"
                                  : "ri-arrow-down-s-fill text-primary"
                                : "ri-arrow-up-down-line opacity-50"
                            }`}
                          ></i>
                        </th>
                      ))}
                      <th style={{ width: 140 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const behind = Number(row.ZCALC___MonthsBehind || 0);
                      return (
                        <tr key={row.Id}>
                          <td>{row.Id}</td>
                          <td>
                            {row.Company_BusinessName || dash}
                            {Number(row.Company_FlagTest) === 1 && (
                              <Badge color="light" className="text-muted ms-1">
                                test
                              </Badge>
                            )}
                          </td>
                          <td>
                            {row.SubscriptionProduct_NamePrinted || dash}
                            {Number(row.FlagBillingPaused) === 1 && (
                              <>
                                <Badge color="warning" className="ms-1">
                                  Paused
                                </Badge>
                                {row.BillingPausedDateTime && (
                                  <div className="text-muted fs-11">Paused on {fmtDateTime(row.BillingPausedDateTime)}</div>
                                )}
                              </>
                            )}
                          </td>
                          <td>
                            {row.User_UserFullName ? (
                              <>
                                {row.User_UserFullName}
                                <div className="text-muted fs-11">
                                  {row.User_Email}
                                </div>
                              </>
                            ) : (
                              <span className="text-muted">Unassigned</span>
                            )}
                          </td>
                          <td>{fmtDateTime(row.StartDateTime)}</td>
                          <td>
                            {/* Change #100463 - date + "overdue by N days" / "in N days"
                                pill (the months-behind badge only showed after a
                                whole month). */}
                            <RenewalWhen at={row.RenewalDateTime} paused={Number(row.FlagBillingPaused) === 1} />
                            {behind > 0 && (
                              <Badge color="danger" className="ms-1">
                                {behind} month{behind === 1 ? "" : "s"} behind
                              </Badge>
                            )}
                          </td>
                          <td>{fmtDateTime(row.RenewalGraceDateTime)}</td>
                          <td>{fmtDateTime(row.EndDateTime)}</td>
                          <td className="text-end">
                            {fmtMoney(row.AmountLocalTotal)}
                          </td>
                          <td>
                            {row.SubscriptionLicenceDurationType_Name || dash}
                          </td>
                          <td>{fmtDateTime(row.LastRenewalDateTime)}</td>
                          <td>
                            <div className="hstack gap-1">
                              <button
                                type="button"
                                className="btn btn-sm btn-soft-info"
                                title="Edit"
                                onClick={() => openEditor(row)}
                              >
                                <i className="ri-pencil-fill"></i>
                              </button>
                              {Number(row.FlagBillingPaused) === 1 ? (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-soft-success"
                                  title="Resume billing"
                                  onClick={() => {
                                    setResumeDate(new Date().toISOString().slice(0, 10));
                                    setPauseTarget({ row, resume: true });
                                  }}
                                >
                                  <i className="ri-play-fill"></i>
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-soft-warning"
                                  title="Pause billing (the card run will not charge it)"
                                  onClick={() => setPauseTarget({ row, resume: false })}
                                >
                                  <i className="ri-pause-fill"></i>
                                </button>
                              )}
                              {!(row.EndDateTime && new Date(String(row.EndDateTime).replace(" ", "T")) <= new Date()) && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-soft-danger"
                                  title="Cancel: end this licence on a date"
                                  onClick={() => {
                                    setCancelDate(new Date().toISOString().slice(0, 10));
                                    setCancelTarget(row);
                                  }}
                                >
                                  <i className="ri-close-circle-fill"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Change #100463 (Paul: "a summary of dues at the bottom") - totals
                  for everything on this tab (all pages), from the server. */}
              {totals && Number(totals.LicenceQty) > 0 && (
                <div className="border rounded-3 p-3 mt-3 bg-light">
                  <div className="fw-semibold mb-2">
                    {tab === "overdue"
                      ? "Overdue: what the card run will collect"
                      : tab === "due7"
                      ? "Due in the next 7 days"
                      : "Summary"}
                  </div>
                  <Row className="g-3">
                    <Col xs={6} md={2}>
                      <div className="text-muted fs-12">Licences</div>
                      <div className="fs-5 fw-bold">{Number(totals.LicenceQty).toLocaleString()}</div>
                    </Col>
                    <Col xs={6} md={2}>
                      <div className="text-muted fs-12">Companies</div>
                      <div className="fs-5 fw-bold">{Number(totals.CompanyQty).toLocaleString()}</div>
                    </Col>
                    <Col xs={6} md={2}>
                      <div className="text-muted fs-12">{tab === "due7" ? "Due (ex VAT)" : "One period (ex VAT)"}</div>
                      <div className="fs-5 fw-bold">{fmtMoney(totals.AmountLocalSubTotal)}</div>
                    </Col>
                    <Col xs={6} md={2}>
                      <div className="text-muted fs-12">VAT</div>
                      <div className="fs-5 fw-bold">{fmtMoney(totals.AmountLocalVat)}</div>
                    </Col>
                    <Col xs={6} md={2}>
                      <div className="text-muted fs-12">{tab === "due7" ? "Due (inc VAT)" : "One period (inc VAT)"}</div>
                      <div className="fs-5 fw-bold">{fmtMoney(totals.AmountLocalTotal)}</div>
                    </Col>
                    {Number(totals.OverdueQty) > 0 && (
                      <Col xs={6} md={2}>
                        <div className="text-muted fs-12">Arrears due now (inc VAT)</div>
                        <div className="fs-5 fw-bold text-danger">{fmtMoney(totals.OverdueAmountLocalTotal)}</div>
                        <div className="text-muted fs-11">
                          {Number(totals.OverdueQty)} licence{Number(totals.OverdueQty) === 1 ? "" : "s"},{" "}
                          {Number(totals.OverduePeriods)} month{Number(totals.OverduePeriods) === 1 ? "" : "s"} ({fmtMoney(totals.OverdueAmountLocalSubTotal)} ex VAT)
                        </div>
                      </Col>
                    )}
                  </Row>
                  {Number(totals.NotCollectedAmountLocalTotal) > 0 && (
                    <div className="text-muted fs-12 mt-2">
                      <i className="ri-error-warning-line me-1 text-warning"></i>
                      {fmtMoney(totals.NotCollectedAmountLocalTotal)} is overdue on annual licences, which the card run
                      does not collect automatically.
                    </div>
                  )}
                  {tab !== "paused" && (
                    <div className="text-muted fs-11 mt-1">
                      Paused licences are left out of the arrears. Arrears = each monthly licence times every month it is
                      behind, as the nightly card run bills them.
                    </div>
                  )}
                </div>
              )}

              {/* Change #100463 - "Show N results" at the foot of the table, like
                  the other tables, always shown; paging only when needed. */}
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                <div className="d-flex align-items-center gap-2 fs-13">
                  <span className="text-muted">Show</span>
                  <Input
                    type="select"
                    bsSize="sm"
                    style={{ width: 80 }}
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Input>
                  <span className="text-muted">results</span>
                </div>
                {pageQty > 1 && (
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      color="light"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <span className="text-muted fs-13">
                      Page {page} of {pageQty}
                    </span>
                    <Button
                      color="light"
                      size="sm"
                      disabled={page >= pageQty}
                      onClick={() => setPage((p) => Math.min(pageQty, p + 1))}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <AppModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        size="lg"
        title={
          editing
            ? `Edit Licence #${editing["Id"]} — ${
                editing.Company_BusinessName || ""
              }`
            : "New Subscription Licence"
        }
        subtitle="SubscriptionLicence"
        loading={saving}
        loadingText="Saving…"
        onSubmit={save}
        submitLabel="Save"
      >
        <Row className="g-3">
          <Col md={6}>
            <label className="form-label mb-1 fs-13">
              Company<span className="text-danger"> *</span>
            </label>
            {editing ? (
              <Input
                type="text"
                readOnly
                value={
                  editing.Company_BusinessName || companyName(form.Company_Id)
                }
              />
            ) : (
              <Input
                type="select"
                value={form.Company_Id}
                disabled={!!fixedCompanyId}
                onChange={(e) => setField("Company_Id", e.target.value)}
              >
                <option value="">Choose a company…</option>
                {options.company_list.map((c) => (
                  <option key={c.Id} value={c.Id}>
                    {c.BusinessName}
                    {Number(c.FlagTest) === 1 ? " (test)" : ""}
                  </option>
                ))}
              </Input>
            )}
          </Col>

          <Col md={6}>
            <label className="form-label mb-1 fs-13">
              Subscription Product<span className="text-danger"> *</span>
            </label>
            {editing ? (
              <Input
                type="text"
                readOnly
                value={editing.SubscriptionProduct_NamePrinted || ""}
              />
            ) : (
              <Input
                type="select"
                value={form.SubscriptionProduct_Id}
                onChange={(e) =>
                  setField("SubscriptionProduct_Id", e.target.value)
                }
              >
                <option value="">Choose a product…</option>
                {options.subscriptionproduct_list.map((p) => (
                  <option key={p.Id} value={p.Id}>
                    {p.NamePrinted}
                  </option>
                ))}
              </Input>
            )}
          </Col>

          <Col md={8}>
            <label className="form-label mb-1 fs-13">Assigned to</label>
            <AppSelect
              isClearable
              isSearchable
              isLoading={companyUsersLoading}
              isDisabled={!form.Company_Id}
              placeholder={form.Company_Id ? "Search this company's users..." : "Choose the company first"}
              options={companyUsers}
              value={
                form.User_Id
                  ? companyUsers.find((o) => o.value === String(form.User_Id)) || {
                      value: String(form.User_Id),
                      label: editing?.User_UserFullName
                        ? `${editing.User_UserFullName}${editing.User_Email ? " (" + editing.User_Email + ")" : ""}`
                        : `User #${form.User_Id}`,
                    }
                  : null
              }
              onChange={(opt) => setField("User_Id", opt ? opt.value : "")}
              noOptionsMessage={() => "No users in this company"}
            />
            <div className="mt-1">
              {form.User_Id ? (
                <span className="badge badge-soft-success">Assigned</span>
              ) : (
                <span className="badge badge-soft-info">Free to assign to a new person</span>
              )}
            </div>
          </Col>

          <Col md={4}>
            <label className="form-label mb-1 fs-13">Duration</label>
            <Input
              type="select"
              value={form.SubscriptionLicenceDurationType_Id}
              onChange={(e) =>
                setField("SubscriptionLicenceDurationType_Id", e.target.value)
              }
            >
              <option value="">—</option>
              {options.subscriptionlicencedurationtype_list.map((d) => (
                <option key={d.Id} value={d.Id}>
                  {d.Name}
                </option>
              ))}
            </Input>
          </Col>

          <Col md={12}>
            <hr className="my-1" />
            <span className="text-muted fs-12">Dates</span>
          </Col>

          <Col md={6}>
            <label className="form-label mb-1 fs-13">Start Date</label>
            <Input
              type="datetime-local"
              value={form.StartDateTime}
              onChange={(e) => setField("StartDateTime", e.target.value)}
            />
          </Col>

          <Col md={6}>
            <label className="form-label mb-1 fs-13">
              End Date
              <Button
                color="link"
                className="p-0 ms-2 fs-12 align-baseline"
                onClick={() => setField("EndDateTime", nowForInput())}
              >
                end it now
              </Button>
            </label>
            <Input
              type="datetime-local"
              value={form.EndDateTime}
              onChange={(e) => setField("EndDateTime", e.target.value)}
            />
            <small className="text-muted fs-11">
              Setting this stops the nightly renewal run picking the licence up.
              The row is kept.
            </small>
          </Col>

          <Col md={6}>
            <label className="form-label mb-1 fs-13">Next Renewal Date</label>
            <Input
              type="datetime-local"
              value={form.RenewalDateTime}
              onChange={(e) => setField("RenewalDateTime", e.target.value)}
            />
            <small className="text-muted fs-11">
              A past date on a live licence bills the arrears on the next run:
              one line per outstanding period, on one invoice. Allowed, but you
              will be asked to confirm.
            </small>
          </Col>

          <Col md={3}>
            <label className="form-label mb-1 fs-13">Grace Date</label>
            <Input
              type="datetime-local"
              value={form.RenewalGraceDateTime}
              onChange={(e) => setField("RenewalGraceDateTime", e.target.value)}
            />
          </Col>

          <Col md={3}>
            <label className="form-label mb-1 fs-13">Grace Days</label>
            <Input
              type="number"
              value={form.RenewalGraceDays}
              onChange={(e) => setField("RenewalGraceDays", e.target.value)}
            />
          </Col>

          <Col md={12}>
            <hr className="my-1" />
            <span className="text-muted fs-12">
              Pricing
              <Button
                color="link"
                className="p-0 ms-2 fs-12 align-baseline"
                onClick={recalc}
              >
                recalculate VAT and total
              </Button>
            </span>
          </Col>

          <Col md={3}>
            <label className="form-label mb-1 fs-13">Sub Total</label>
            <Input
              type="number"
              step="any"
              value={form.AmountLocalSubTotal}
              onChange={(e) => setField("AmountLocalSubTotal", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <label className="form-label mb-1 fs-13">VAT %</label>
            <Input
              type="number"
              step="any"
              value={form.AmountVatPercentage}
              onChange={(e) => setField("AmountVatPercentage", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <label className="form-label mb-1 fs-13">VAT</label>
            <Input
              type="number"
              step="any"
              value={form.AmountLocalVat}
              onChange={(e) => setField("AmountLocalVat", e.target.value)}
            />
          </Col>
          <Col md={3}>
            <label className="form-label mb-1 fs-13">Total</label>
            <Input
              type="number"
              step="any"
              value={form.AmountLocalTotal}
              onChange={(e) => setField("AmountLocalTotal", e.target.value)}
            />
          </Col>

          <Col md={12}>
            <hr className="my-1" />
            <span className="text-muted fs-12">Payment retries</span>
          </Col>

          <Col md={4}>
            <label className="form-label mb-1 fs-13">Attempts Maximum</label>
            <Input
              type="number"
              value={form.PaymentRetryAttemptsMaximum}
              onChange={(e) =>
                setField("PaymentRetryAttemptsMaximum", e.target.value)
              }
            />
          </Col>
          <Col md={4}>
            <label className="form-label mb-1 fs-13">Attempts Remaining</label>
            <Input
              type="number"
              value={form.PaymentRetryAttemptsRemaining}
              onChange={(e) =>
                setField("PaymentRetryAttemptsRemaining", e.target.value)
              }
            />
            <small className="text-muted fs-11">
              0 takes the licence out of the renewal run entirely.
            </small>
          </Col>
          <Col md={4}>
            <label className="form-label mb-1 fs-13">Next Retry</label>
            <Input
              type="datetime-local"
              value={form.PaymentRetryAttemptNextDateTime}
              onChange={(e) =>
                setField("PaymentRetryAttemptNextDateTime", e.target.value)
              }
            />
          </Col>
        </Row>
      </AppModal>
      <AppModal
        open={!!cancelTarget}
        onClose={() => !cancelling && setCancelTarget(null)}
        title="Cancel licence"
        subtitle={cancelTarget ? `#${cancelTarget.Id} ${cancelTarget.SubscriptionProduct_NamePrinted || ""} · ${cancelTarget.Company_BusinessName || ""}` : ""}
        icon="ri-close-circle-line"
        headerAccent="indigo"
        size="sm"
        onSubmit={saveCancel}
        submitLabel="Cancel licence"
        submitDisabled={cancelling}
        loading={cancelling}
        loadingText="Cancelling..."
      >
        <div>
          <label className="form-label mb-1">End date</label>
          <Input type="date" value={cancelDate} onChange={(e) => setCancelDate(e.target.value)} />
          <div className="text-muted fs-12 mt-2">
            The licence stops after this day: no more renewals are charged and it stops giving access
            {cancelTarget?.User_UserFullName ? ` (${cancelTarget.User_UserFullName} loses it then)` : ""}. The row is kept as
            history on the Expired tab.
          </div>
        </div>
      </AppModal>

      <AppModal
        open={!!pauseTarget}
        onClose={() => !pauseSaving && setPauseTarget(null)}
        title={pauseTarget?.resume ? "Resume billing" : "Pause billing"}
        subtitle={pauseTarget ? `#${pauseTarget.row.Id} ${pauseTarget.row.SubscriptionProduct_NamePrinted || ""} · ${pauseTarget.row.Company_BusinessName || ""}` : ""}
        icon={pauseTarget?.resume ? "ri-play-circle-line" : "ri-pause-circle-line"}
        headerAccent="indigo"
        size="sm"
        onSubmit={savePause}
        submitLabel={pauseTarget?.resume ? "Resume" : "Pause"}
        submitDisabled={pauseSaving}
        loading={pauseSaving}
        loadingText="Saving..."
      >
        {pauseTarget?.resume ? (
          <div>
            <label className="form-label mb-1">Next renewal</label>
            <Input type="date" value={resumeDate} onChange={(e) => setResumeDate(e.target.value)} />
            <div className="text-muted fs-12 mt-1">
              Billing restarts from this date. Today or later, so the paused months are not billed as arrears.
            </div>
          </div>
        ) : (
          <p className="mb-0">
            The nightly card run will not invoice or charge this licence until you resume it. Access is not changed
            until its renewal grace runs out.
          </p>
        )}
      </AppModal>
    </React.Fragment>
  );
};

export default SubscriptionLicenceAdmin;
